import React, { useEffect } from "react";
import { gs } from "../../styles/globalStyles";

// ─── Modal ───────────────────────────────────────────────────────────────────
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = "md",
}) => {
  useEffect(() => {
    if (isOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const sizeMap = {
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-2xl",
    xl: "max-w-4xl",
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div
        className={`relative bg-gray-900 rounded-2xl w-full ${sizeMap[size]} border border-gray-800/80 shadow-2xl animate-slide-up`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-gray-800/60">
          <h3 className={gs.h4}>{title}</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:text-gray-50 hover:bg-gray-800 transition-all"
          >
            ✕
          </button>
        </div>
        <div className="p-5">{children}</div>
        {footer && (
          <div className="p-5 border-t border-gray-800/60">{footer}</div>
        )}
      </div>
    </div>
  );
};

// ─── Input ───────────────────────────────────────────────────────────────────
// Note: renamed 'prefix' -> 'iconLeft' to avoid conflict with HTML input 'prefix' attribute (string only)
interface InputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "prefix"
> {
  label?: string;
  error?: string;
  hint?: string;
  prefix?: React.ReactNode; // icon on the left
  suffix?: React.ReactNode;
}

export const Input: React.FC<InputProps> = ({
  label,
  error,
  hint,
  prefix,
  suffix,
  className = "",
  ...props
}) => (
  <div className="w-full">
    {label && <label className={gs.label}>{label}</label>}
    <div className="relative">
      {prefix && (
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
          {prefix}
        </div>
      )}
      <input
        className={`${error ? gs.inputError : gs.input} ${prefix ? "pl-10" : ""} ${suffix ? "pr-10" : ""} ${className}`}
        {...props}
      />
      {suffix && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
          {suffix}
        </div>
      )}
    </div>
    {error && (
      <p className={gs.error}>
        <span>⚠</span> {error}
      </p>
    )}
    {hint && !error && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
  </div>
);

// ─── Select ──────────────────────────────────────────────────────────────────
interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  options: { value: string | number; label: string }[];
}

export const Select: React.FC<SelectProps> = ({
  label,
  error,
  options,
  className = "",
  ...props
}) => (
  <div className="w-full">
    {label && <label className={gs.label}>{label}</label>}
    <div className="relative">
      <select className={`${gs.select} ${className}`} {...props}>
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
      <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
        ▾
      </div>
    </div>
    {error && (
      <p className={gs.error}>
        <span>⚠</span> {error}
      </p>
    )}
  </div>
);

// ─── Tabs ────────────────────────────────────────────────────────────────────
interface TabsProps {
  tabs: { id: string; label: string; icon?: React.ReactNode; count?: number }[];
  active: string;
  onChange: (id: string) => void;
  className?: string;
}

export const Tabs: React.FC<TabsProps> = ({
  tabs,
  active,
  onChange,
  className = "",
}) => (
  <div
    className={`flex gap-1 bg-gray-900/60 rounded-xl p-1 border border-gray-800/50 ${className}`}
  >
    {tabs.map((tab) => (
      <button
        key={tab.id}
        onClick={() => onChange(tab.id)}
        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-display font-semibold transition-all duration-200 ${
          active === tab.id
            ? "bg-primary text-[var(--on-primary)] shadow-glow-sm"
            : "text-gray-400 hover:text-gray-50"
        }`}
      >
        {tab.icon}
        {tab.label}
        {tab.count !== undefined && (
          <span
            className={`text-xs px-1.5 py-0.5 rounded-full font-mono ${active === tab.id ? "bg-black/20" : "bg-gray-800"}`}
          >
            {tab.count}
          </span>
        )}
      </button>
    ))}
  </div>
);

// ─── Alert ───────────────────────────────────────────────────────────────────
export interface AlertProps {
  type?: "success" | "error" | "info" | "warning";
  title?: string;
  message: string;
  onClose?: () => void;
  className?: string;
}

export const Alert: React.FC<AlertProps> = ({
  type = "info",
  title,
  message,
  onClose,
  className = "",
}) => {
  const cfg = {
    success: {
      bg: "bg-primary/10 border-primary/30",
      text: "text-primary",
      icon: "✓",
    },
    error: {
      bg: "bg-red-500/10 border-red-500/30",
      text: "text-red-500",
      icon: "✕",
    },
    warning: {
      bg: "bg-yellow-500/10 border-yellow-500/30",
      text: "text-yellow-400",
      icon: "⚠",
    },
    info: {
      bg: "bg-blue-500/10 border-blue-500/30",
      text: "text-blue-400",
      icon: "ℹ",
    },
  }[type];

  return (
    <div
      className={`flex gap-3 p-4 rounded-xl border ${cfg.bg} animate-fade-in ${className}`}
    >
      <span className={`${cfg.text} font-bold text-lg leading-none mt-0.5`}>
        {cfg.icon}
      </span>
      <div className="flex-1">
        {title && (
          <p className={`${cfg.text} font-semibold text-sm mb-1`}>{title}</p>
        )}
        <p className="text-gray-300 text-sm">{message}</p>
      </div>
      {onClose && (
        <button onClick={onClose} className="text-gray-500 hover:text-gray-50">
          ✕
        </button>
      )}
    </div>
  );
};
